/**
 * Before and after, word by word.
 *
 * What a writer accepts in a regulated document is a *change*, not a new
 * paragraph — "nothing changes until you accept" is only a real promise if
 * what is being accepted can be read at a glance. So every resolve proposal the
 * add-in shows for review is rendered as a diff first.
 *
 * Word-level rather than character-level: a character diff of prose marks the
 * inside of words and reads as noise. Words are also the unit a reviewer
 * argues about ("that should say *median*"), which is the unit worth
 * highlighting.
 *
 * Pure, so the derivation is node-testable and the cards stay renderers. This
 * is a verbatim port of the web editor's ``word-diff.ts`` (asthra-v4-web) — the
 * add-in has no ProseMirror, so it lifts only the compute and renders it with
 * its own Fluent DiffView (see ``components/DiffView.tsx``).
 */

export type DiffSegment = {
  readonly kind: "same" | "added" | "removed";
  readonly text: string;
};

/**
 * Split into words, each carrying the whitespace that follows it.
 *
 * Re-joining the tokens must reproduce the text exactly, and a run of changed
 * words must highlight as one run — attaching the trailing space to its word
 * satisfies both.
 */
const tokenize = (text: string): readonly string[] => {
  const leading = /^\s+/u.exec(text)?.[0] ?? "";
  const words = text.slice(leading.length).match(/\S+\s*/gu) ?? [];
  return leading === "" ? words : [leading, ...words];
};

/**
 * What a token is compared *as* — the token, plus the single space it would
 * carry if the text kept going. Normalising only the absent trailing space
 * keeps real spacing significant.
 */
const keyOf = (token: string): string => (/\s$/u.test(token) ? token : `${token} `);

/**
 * The longest common subsequence of two token lists, as a table walk.
 * Quadratic, bounded below; past the bound the diff degrades to "everything
 * replaced", which is honest for a near-total rewrite.
 */
const MAX_TOKENS = 4_000;

export function wordDiff(before: string, after: string): readonly DiffSegment[] {
  if (before === after) return before === "" ? [] : [{ kind: "same", text: before }];

  const a = tokenize(before);
  const b = tokenize(after);

  if (a.length === 0) return b.length === 0 ? [] : [{ kind: "added", text: after }];
  if (b.length === 0) return [{ kind: "removed", text: before }];
  if (a.length * b.length > MAX_TOKENS * MAX_TOKENS) {
    return [
      { kind: "removed", text: before },
      { kind: "added", text: after },
    ];
  }

  // Alignment runs on the keys; emission always uses the raw tokens.
  const ka = a.map(keyOf);
  const kb = b.map(keyOf);

  // lengths[i][j] — the LCS of a[i…] and b[j…]. Built backwards so the walk
  // forward below can follow the larger of the two neighbours.
  const lengths: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lengths[i]![j] =
        ka[i] === kb[j]
          ? (lengths[i + 1]![j + 1] ?? 0) + 1
          : Math.max(lengths[i + 1]![j] ?? 0, lengths[i]![j + 1] ?? 0);
    }
  }

  const segments: DiffSegment[] = [];
  // Runs are coalesced as they are emitted: three consecutive added words are
  // one highlight, not three.
  const push = (kind: DiffSegment["kind"], text: string) => {
    if (text === "") return;
    const last = segments[segments.length - 1];
    if (last !== undefined && last.kind === kind) {
      segments[segments.length - 1] = { kind, text: last.text + text };
      return;
    }
    segments.push({ kind, text });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (ka[i] === kb[j]) {
      const at = a[i]!;
      const bt = b[j]!;
      if (at === bt) {
        push("same", at);
      } else {
        // Same word, different trailing whitespace — only ever the end of one
        // side. The word is common; the spacing is the change.
        const core = at.replace(/\s+$/u, "");
        push("same", core);
        push("removed", at.slice(core.length));
        push("added", bt.slice(core.length));
      }
      i += 1;
      j += 1;
    } else if ((lengths[i + 1]![j] ?? 0) >= (lengths[i]![j + 1] ?? 0)) {
      push("removed", a[i]!);
      i += 1;
    } else {
      push("added", b[j]!);
      j += 1;
    }
  }
  while (i < a.length) {
    push("removed", a[i]!);
    i += 1;
  }
  while (j < b.length) {
    push("added", b[j]!);
    j += 1;
  }

  return segments;
}

/** Whether a proposal actually proposes anything. */
export const isNoOp = (before: string, after: string): boolean => before.trim() === after.trim();

/** How much a proposal touches, for a one-line summary above the diff. */
export function diffSummary(segments: readonly DiffSegment[]): {
  readonly added: number;
  readonly removed: number;
} {
  let added = 0;
  let removed = 0;
  for (const segment of segments) {
    // Whitespace-only runs are not words changed.
    const words = segment.text.trim() === "" ? 0 : segment.text.trim().split(/\s+/u).length;
    if (segment.kind === "added") added += words;
    if (segment.kind === "removed") removed += words;
  }
  return { added, removed };
}
